import { memo } from "react";

import type {
	GuestPageVisibilityMode,
	PlainFontFamily,
	PlainReservationComponentType,
	PlainSizeType,
	PlainWidgetDecoration,
	PlainWidgetItemInput,
	S3Object,
} from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types";
import type * as FormModelType from "@/components/features/design/plain/model/type";

import { GuestPageVisibilityQuestion } from "@/components/features-new/guest-page-visibility";

import { AnalysisSettingForm } from "./analysis-setting";
import { BasicSettingForm } from "./basic-setting";
import { ContentsSettingForm } from "./contents-setting";
import { FormIslandSection } from "./island-section";
import { NewReleaseNote } from "./new-release-note";
import { UrlSettingForm } from "./url-setting";

type Props = {
	hotelID: string;

	/** 最初の登録が完了しているかどうか */
	isFirstRegistrationCompleted: boolean;

	/** モバイル用のプレビューペイン。URL設定の下に表示される。 */
	mobilePreview?: React.ReactNode;

	// urlSettingForm
	domainName: string;
	subDomainName: string;
	subDomainNameErrorMessage?: string;
	onChangeDomain: (subDomainName: string) => void;

	// guestPageVisibility
	guestPageVisibility: GuestPageVisibilityMode;
	onChangeGuestPageUnlisted: (isUnlisted: boolean) => void;

	// basicSettingForm
	// color
	backgroundColor: ForNodeType<string>;
	onChangeColorBackground: (value: string) => void;
	primaryColor: ForNodeType<string>;
	onChangeColorPrimary: (value: string) => void;
	fontColor: ForNodeType<string>;
	onChangeColorFont: (value: string) => void;
	onApplyRecommendationColors: (colorBackground: string, colorPrimary: string, colorFont: string) => void;
	// hotelInformation
	logoImage: FormModelType.PlainSettingFormModel["ViewModel"]["logoImage"];
	logoSize: PlainSizeType;
	isShowHotelName: boolean;
	isShowHotelCatchCopy: boolean;
	isShowHotelLogo: boolean;
	isShowHotelKeyVisual: boolean;
	onChangeLogo: (value: S3Object) => void;
	onChangeLogoSize: (value: PlainSizeType) => void;
	onChangeIsShowHotelName: (value: boolean) => void;
	onChangeIsShowHotelCatchCopy: (value: boolean) => void;
	onChangeIsShowHotelLogo: (value: boolean) => void;
	onChangeIsShowHotelKeyVisual: (value: boolean) => void;
	// fontFamily
	plainFontFamily: PlainFontFamily;
	onChangeFontFamily: (value: PlainFontFamily) => void;
	// reservationComponent
	reservationComponentType: PlainReservationComponentType;
	onChangeReservationComponentType: (value: PlainReservationComponentType) => void;

	// contentsSettingForm
	// snack
	widgetDecoration: PlainWidgetDecoration;
	snacks: FormModelType.QueryData["snackList"]["snacks"];
	plainWidgetItems: Array<PlainWidgetItemInput>;
	onAddPlainWidgetDefault: (contentID: string, title: string) => void;
	onRemovePlainWidgetDefault: (widgetID: string) => void;
	onUpdateOrderPlainWidgets: (widgetIDs: Array<string>) => void;
	onChangeWidgetDecoration: (value: PlainWidgetDecoration) => void;
	// magazine
	isShowMagazineAbout: boolean;
	isShowMagazineRoom: boolean;
	isShowMagazinePlan: boolean;
	onChangeIsShowMagazineAbout: (value: boolean) => void;
	onChangeIsShowMagazineRoom: (value: boolean) => void;
	onChangeIsShowMagazinePlan: (value: boolean) => void;

	// analysisSettingForm
	gtmId?: string;
	gtmIdErrorMessage?: string;
	onChangeGtmID: (value: string) => void;
};

export const MainForm = memo(
	({
		hotelID,
		isFirstRegistrationCompleted,
		primaryColor,
		fontColor,

		// urlSettingForm
		domainName,
		subDomainName,
		subDomainNameErrorMessage,
		onChangeDomain,

		// guestPageVisibility
		guestPageVisibility,
		onChangeGuestPageUnlisted,

		// basicSettingForm
		// color
		backgroundColor,
		onChangeColorBackground,
		onChangeColorPrimary,
		onChangeColorFont,
		onApplyRecommendationColors,
		// hotelInformation
		logoImage,
		logoSize,
		isShowHotelName,
		isShowHotelCatchCopy,
		isShowHotelLogo,
		isShowHotelKeyVisual,
		onChangeLogo,
		onChangeLogoSize,
		onChangeIsShowHotelName,
		onChangeIsShowHotelCatchCopy,
		onChangeIsShowHotelLogo,
		onChangeIsShowHotelKeyVisual,
		// fontFamily
		plainFontFamily,
		onChangeFontFamily,
		// reservationComponent
		reservationComponentType,
		onChangeReservationComponentType,

		// contentsSettingForm
		// snack
		widgetDecoration,
		snacks,
		plainWidgetItems,
		onAddPlainWidgetDefault,
		onRemovePlainWidgetDefault,
		onUpdateOrderPlainWidgets,
		onChangeWidgetDecoration,
		// magazine
		isShowMagazineAbout,
		isShowMagazineRoom,
		isShowMagazinePlan,
		onChangeIsShowMagazineAbout,
		onChangeIsShowMagazineRoom,
		onChangeIsShowMagazinePlan,

		// mobilePreview
		mobilePreview,

		// analysisSettingForm
		gtmId,
		gtmIdErrorMessage,
		onChangeGtmID,
	}: Props) => {
		return (
			<div className="min-w-0 flex-auto space-y-10">
				{!isFirstRegistrationCompleted && <NewReleaseNote />}

				<UrlSettingForm
					domainName={domainName}
					subDomainName={subDomainName}
					errorMessage={subDomainNameErrorMessage}
					onChangeDomain={onChangeDomain}
				/>

				<FormIslandSection title="検索エンジンへの表示" showChildrenDivider={false}>
					<GuestPageVisibilityQuestion
						visibility={guestPageVisibility}
						onChangeUnlisted={onChangeGuestPageUnlisted}
					/>
				</FormIslandSection>

				{mobilePreview && (
					<div className="overflow-hidden rounded-xl border border-zinc-200 laptop:hidden">
						{mobilePreview}
					</div>
				)}

				<BasicSettingForm
					hotelID={hotelID}
					primaryColor={primaryColor}
					fontColor={fontColor}
					// color
					backgroundColor={backgroundColor}
					onChangeColorBackground={onChangeColorBackground}
					onChangeColorPrimary={onChangeColorPrimary}
					onChangeColorFont={onChangeColorFont}
					onApplyRecommendationColors={onApplyRecommendationColors}
					// hotelInformation
					logoImage={logoImage}
					logoSize={logoSize}
					isShowHotelName={isShowHotelName}
					isShowHotelCatchCopy={isShowHotelCatchCopy}
					isShowHotelLogo={isShowHotelLogo}
					isShowHotelKeyVisual={isShowHotelKeyVisual}
					onChangeLogo={onChangeLogo}
					onChangeLogoSize={onChangeLogoSize}
					onChangeIsShowHotelName={onChangeIsShowHotelName}
					onChangeIsShowHotelCatchCopy={onChangeIsShowHotelCatchCopy}
					onChangeIsShowHotelLogo={onChangeIsShowHotelLogo}
					onChangeIsShowHotelKeyVisual={onChangeIsShowHotelKeyVisual}
					// fontFamily
					plainFontFamily={plainFontFamily}
					onChangeFontFamily={onChangeFontFamily}
					// reservationComponent
					reservationComponentType={reservationComponentType}
					onChangeReservationComponentType={onChangeReservationComponentType}
				/>

				<ContentsSettingForm
					hotelID={hotelID}
					// snack
					primaryColor={primaryColor}
					widgetDecoration={widgetDecoration}
					snacks={snacks}
					plainWidgetItems={plainWidgetItems}
					onAddPlainWidgetDefault={onAddPlainWidgetDefault}
					onRemovePlainWidgetDefault={onRemovePlainWidgetDefault}
					onUpdateOrderPlainWidgets={onUpdateOrderPlainWidgets}
					onChangeWidgetDecoration={onChangeWidgetDecoration}
					// magazine
					isShowMagazineAbout={isShowMagazineAbout ?? false}
					isShowMagazinePlan={isShowMagazinePlan ?? false}
					isShowMagazineRoom={isShowMagazineRoom ?? false}
					onChangeIsShowMagazineAbout={onChangeIsShowMagazineAbout}
					onChangeIsShowMagazinePlan={onChangeIsShowMagazinePlan}
					onChangeIsShowMagazineRoom={onChangeIsShowMagazineRoom}
				/>

				<AnalysisSettingForm gtmId={gtmId} errorMessage={gtmIdErrorMessage} onChangeGtmID={onChangeGtmID} />

				{isFirstRegistrationCompleted && <NewReleaseNote />}
			</div>
		);
	},
);
